import React, { useState, useEffect, useContext } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Divider,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ForumIcon from '@mui/icons-material/Forum';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import { ProjectCommunication } from '../../model';
import axios from '../../../sharedModules/utils/axios.utils';
import { AuthenticationContext } from '../../../homeModules/auth/contexts/AuthenticationContext';

interface ExecutionStatusesResponse {
  statuses: string[];
}

interface ContactOption {
  id: number;
  full_name: string;
}

const ProjectCommunicationNewPage: React.FunctionComponent = () => {
  const { servmanProjectCommunicationId } = useParams<{ servmanProjectCommunicationId: string }>();
  const history = useHistory();
  const { user } = useContext(AuthenticationContext);

  const [communication, setCommunication] = useState<ProjectCommunication | null>(null);
  const [pageLoading, setPageLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<boolean>(false);

  const [executionStatuses, setExecutionStatuses] = useState<string[]>([
    'Booking',
    'Turnover',
    'Engineering',
    'Equipment Ordered',
    'Scope Scheduled',
    'Execution',
    'Subcontractor',
    'Startup',
    'Punchlist',
    'Closeout',
    'Job Complete',
    'PO Outstanding',
    'Warranty',
  ]);

  const [contacts, setContacts] = useState<ContactOption[]>([]);

  const [formValues, setFormValues] = useState({
    U_EXSTATUS: '',
    NEW_NOTES: '',
    JOB_NOTES: '',
    CONTACT: '',
  });

  useEffect(() => {
    const fetchCommunication = async () => {
      setPageLoading(true);
      try {
        const response = await axios.get<string, any>(
          `/customer_issues/servman_project_communications/${servmanProjectCommunicationId}`,
        );
        if (response?.data) {
          setCommunication(response.data);
        }
      } catch (err: any) {
        console.error('Error fetching project communication:', err);
        setError('Failed to load project communication data.');
      } finally {
        setPageLoading(false);
      }
    };

    const fetchExecutionStatuses = async () => {
      try {
        const response = await axios.get<ExecutionStatusesResponse>('/admin/job_execution_statuses');
        if (response?.data?.statuses && response.data.statuses.length > 0) {
          setExecutionStatuses(response.data.statuses);
        }
      } catch (err) {
        console.log('Using default execution statuses');
      }
    };

    if (servmanProjectCommunicationId) {
      fetchCommunication();
    }
    fetchExecutionStatuses();
  }, [servmanProjectCommunicationId]);

  useEffect(() => {
    const fetchContacts = async () => {
      if (!communication?.acct_no) return;
      try {
        const response = await axios.get<ContactOption[]>(`/customer_issues/servman_project_communications/contacts`, {
          params: { acct_no: communication.acct_no },
        });
        if (response?.data) {
          setContacts(response.data);
        }
      } catch (err) {
        console.log('Could not load contacts');
      }
    };
    fetchContacts();
  }, [communication]);

  const handleBackClick = () => {
    history.push('/project_communications/search');
  };

  const handleSubmit = async () => {
    if (!formValues.U_EXSTATUS && !formValues.NEW_NOTES && !formValues.JOB_NOTES && !formValues.CONTACT) {
      setError('Please fill in at least one field before submitting.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await axios.post(`/customer_issues/servman_project_communications`, {
        project_communication: {
          servman_project_communication_id: servmanProjectCommunicationId,
          U_EXSTATUS: formValues.U_EXSTATUS,
          NEW_NOTES: formValues.NEW_NOTES,
          JOB_NOTES: formValues.JOB_NOTES,
          CONTACT: formValues.CONTACT,
        },
      });

      setSuccess(true);
      setTimeout(() => {
        history.push('/project_communications/search');
      }, 1500);
    } catch (err: any) {
      console.error('Error creating project communication:', err);
      setError(err.response?.data?.error || 'Failed to create project communication. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body1"
          onClick={handleBackClick}
          sx={{ cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
        >
          Project Communications
        </Link>
        <Typography color="text.primary">New Communication</Typography>
      </Breadcrumbs>

      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ForumIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" fontWeight="bold" sx={{ mb: 0.5 }}>
              New Communication Record
            </Typography>
            {communication && (
              <Typography variant="body2" color="text.secondary">
                {communication.customer} &mdash; Job {communication.job}
              </Typography>
            )}
          </Box>
        </Box>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBackClick}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
        >
          Back to List
        </Button>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {pageLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {!pageLoading && (
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <CardContent sx={{ p: 4 }}>
            {communication && (
              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Project Summary
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Customer:</strong> {communication.customer || '-'}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Job:</strong> {communication.job || '-'}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Department:</strong> {communication.department || '-'} | <strong>Salesperson:</strong>{' '}
                  {communication.salesperson || '-'}
                </Typography>
                <Typography variant="body2">
                  <strong>Opened:</strong> {communication.opened || '-'}
                </Typography>
              </Box>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircleIcon />}>
                Project communication created successfully!
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, mt: 1 }}>
                  Job Status
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Job Execution Status</InputLabel>
                  <Select
                    value={formValues.U_EXSTATUS}
                    label="Job Execution Status"
                    onChange={(e) => setFormValues({ ...formValues, U_EXSTATUS: e.target.value })}
                    disabled={submitting || success}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {executionStatuses.map((status) => (
                      <MenuItem key={status} value={status}>
                        {status}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Contact</InputLabel>
                  <Select
                    value={formValues.CONTACT}
                    label="Contact"
                    onChange={(e) => setFormValues({ ...formValues, CONTACT: e.target.value })}
                    disabled={submitting || success}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {contacts.map((contact) => (
                      <MenuItem key={contact.id} value={contact.full_name}>
                        {contact.full_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, mt: 3 }}>
                  Communication
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Communication Notes"
                  fullWidth
                  multiline
                  rows={4}
                  value={formValues.NEW_NOTES}
                  onChange={(e) => setFormValues({ ...formValues, NEW_NOTES: e.target.value })}
                  disabled={submitting || success}
                  helperText="Enter initial communication notes for this project"
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, mt: 3 }}>
                  Job Notes
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Job Notes"
                  fullWidth
                  multiline
                  rows={4}
                  value={formValues.JOB_NOTES}
                  onChange={(e) => setFormValues({ ...formValues, JOB_NOTES: e.target.value })}
                  disabled={submitting || success}
                  helperText="Enter initial job notes"
                />
              </Grid>
            </Grid>

            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: 'info.lighter',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'info.light',
              }}
            >
              <Typography variant="caption" color="text.secondary">
                <strong>Note:</strong> This will create a new communication record in ServMan. Changes may take a few
                minutes to appear in the system.
              </Typography>
            </Box>

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button onClick={handleBackClick} disabled={submitting}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                variant="contained"
                color="primary"
                disabled={submitting || success}
                startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              >
                {submitting ? 'Creating...' : 'Create Communication'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default ProjectCommunicationNewPage;
